import { useHotkeys } from '@tanstack/react-hotkeys';
import { useSetAtom } from 'jotai';
import { clearanceViolationStepAtom } from '@/atoms/clearanceNavigation.atom';
import { snapSettingsAtom, undoAtom, redoAtom } from '@/atoms/snapping.atom';

/**
 * Viewer-scoped hotkeys — single-key shortcuts for 3D viewer controls.
 * These are registered against a container ref so they only fire
 * when the viewer is focused (not when typing in inputs).
 */
export const useViewerHotkeys = ({
  onToggleMap,
  onTogglePointcloud,
  onToggleClipBox,
  onToggleOrthoCamera,
  onToggleThirdPerson,
  onToggleStreetView,
  onToggleMeasureDistance,
  onToggleMeasureArea,
  onScreenshot,
  onToggleAnalysisPanels,
  hasAnalysisPanels,
  hasPointclouds,
  hasMeasureableTargets,
  isFlying,
  canToggleCameraMode,
  isGoogleStreetViewEnabled,
  canUseClipBox,
  onStationPrev,
  onStationNext,
  hasStationVolumes,
  canUseStationVolumes,
  hasClearanceDock = false,
}: {
  onToggleMap: () => void;
  onTogglePointcloud: () => void;
  onToggleClipBox: () => void;
  onToggleOrthoCamera: () => void;
  onToggleThirdPerson: () => void;
  onToggleStreetView: () => void;
  onToggleMeasureDistance: () => void;
  onToggleMeasureArea: () => void;
  onScreenshot: () => void;
  // Hide / show the floating analysis UI (column + rainfall legends).
  onToggleAnalysisPanels: () => void;
  hasAnalysisPanels: boolean;
  hasPointclouds: boolean;
  hasMeasureableTargets: boolean;
  isFlying: boolean;
  // Whether the 3rd-person / ortho camera toggles are usable. Unlike `isFlying`
  // these stay available while images are shown — the toggle handlers deselect
  // the IMAGE layers first to drop into flying mode. Only street view blocks them.
  canToggleCameraMode: boolean;
  isGoogleStreetViewEnabled: boolean;
  // Subscription-tier gate. Hotkey is silently disabled when false; the toolbar
  // button surfaces the upgrade prompt instead — see `viewer.clipBox` in
  // app/src/lib/featureGates.ts.
  canUseClipBox: boolean;
  // Step the station-volumes walk cursor one bin back / forward (Shift: ten).
  // `AlignmentWalkToolbar` binds the same two keys to its own walk step and
  // stands down while the dock is open, so exactly one handler ever fires.
  onStationPrev: (mult: number) => void;
  onStationNext: (mult: number) => void;
  // Whether the station-volumes dock is open. Off, `[` / `]` belong to the
  // walk toolbar.
  hasStationVolumes: boolean;
  // Subscription-tier gate, same contract as `canUseClipBox`: the hotkey goes
  // quiet and the dock's own controls surface the upgrade prompt — see
  // `analysis.stationVolumes` in app/src/lib/featureGates.ts.
  canUseStationVolumes: boolean;
  // Whether the CLEARANCE dock is the one open (`stationVolumeDockAtom`'s
  // `dataset === 'clearance'`). While it is, `Shift+[` / `Shift+]` mean
  // "previous / next violating interval" instead of "ten intervals" — the
  // dock's chevron buttons beside "Go to worst". Optional, defaulting off, so
  // a caller that has not wired it keeps the station-volumes meaning.
  //
  // Deliberately NOT tier-gated (round-3 decision 4): violation stepping only
  // moves the reader around results they already have, and the dock's own
  // ◀ ▶ buttons — whose tooltips print these keys — work wherever the dock
  // does. A gate here made the keys silently do nothing beside buttons that
  // worked. The paid parts the dock drives (envelope, clip, render take-over)
  // keep their own gates.
  hasClearanceDock?: boolean;
}) => {
  const undo = useSetAtom(undoAtom);
  const redo = useSetAtom(redoAtom);
  const setSnapSettings = useSetAtom(snapSettingsAtom);
  // The dock owns the bins, so the key only ASKS; `ClearanceDock` answers.
  const requestViolationStep = useSetAtom(clearanceViolationStepAtom);
  const stepTenEnabled = hasStationVolumes && canUseStationVolumes && !hasClearanceDock;
  const violationStepEnabled = hasClearanceDock;

  useHotkeys([
    {
      hotkey: 'm',
      callback: onToggleMap,
      options: { ignoreInputs: true },
    },
    {
      hotkey: 'p',
      callback: onTogglePointcloud,
      options: { ignoreInputs: true, enabled: hasPointclouds },
    },
    {
      hotkey: 'c',
      callback: onToggleClipBox,
      options: { ignoreInputs: true, enabled: canUseClipBox },
    },
    {
      hotkey: 'o',
      callback: onToggleOrthoCamera,
      options: { ignoreInputs: true, enabled: canToggleCameraMode },
    },
    {
      hotkey: 't',
      callback: onToggleThirdPerson,
      options: { ignoreInputs: true, enabled: canToggleCameraMode },
    },
    {
      hotkey: 'g',
      callback: onToggleStreetView,
      options: { ignoreInputs: true, enabled: isGoogleStreetViewEnabled },
    },
    {
      hotkey: 'r',
      callback: onToggleMeasureDistance,
      options: { ignoreInputs: true, enabled: hasMeasureableTargets },
    },
    {
      hotkey: 'b',
      callback: onToggleMeasureArea,
      options: { ignoreInputs: true, enabled: hasMeasureableTargets },
    },
    {
      hotkey: 'i',
      callback: onScreenshot,
      options: { ignoreInputs: true },
    },
    {
      // Uppercase is the library's canonical letter form; matching is
      // case-insensitive, so a plain 'h' press fires it (same as 'S' below).
      hotkey: 'H',
      callback: onToggleAnalysisPanels,
      options: { ignoreInputs: true, enabled: hasAnalysisPanels },
    },
    // ── Station-volumes bin stepping ─────────────────────────────────────────
    // Punctuation keys aren't in the library's modifier-prefixed string union,
    // so the Shift variants use the RawHotkey object form — same caveat as
    // `AlignmentWalkToolbar`: matching falls back to `event.code` via a US
    // physical-layout map.
    {
      hotkey: '[',
      callback: () => onStationPrev(1),
      options: { ignoreInputs: true, enabled: hasStationVolumes && canUseStationVolumes },
    },
    {
      hotkey: ']',
      callback: () => onStationNext(1),
      options: { ignoreInputs: true, enabled: hasStationVolumes && canUseStationVolumes },
    },
    // Shift+[ / Shift+]: ten intervals over the station-volumes dock, the
    // previous / next VIOLATING interval over the clearance dock (the dock's
    // chevrons beside "Go to worst"). ONE registration per key with a
    // branching callback — registering the same key twice, once per meaning,
    // makes the hotkeys library warn about a conflict on every mount.
    {
      hotkey: { key: '[', shift: true },
      callback: () =>
        violationStepEnabled
          ? requestViolationStep((prev) => ({ dir: -1, seq: (prev?.seq ?? 0) + 1 }))
          : onStationPrev(10),
      options: { ignoreInputs: true, enabled: violationStepEnabled || stepTenEnabled },
    },
    {
      hotkey: { key: ']', shift: true },
      callback: () =>
        violationStepEnabled
          ? requestViolationStep((prev) => ({ dir: 1, seq: (prev?.seq ?? 0) + 1 }))
          : onStationNext(10),
      options: { ignoreInputs: true, enabled: violationStepEnabled || stepTenEnabled },
    },
    // ── Undo / redo ──────────────────────────────────────────────────────────
    // Mod resolves to Cmd on macOS and Ctrl on Windows/Linux.
    {
      hotkey: 'Mod+Z',
      callback: () => undo(),
      options: { ignoreInputs: true, preventDefault: true },
    },
    {
      hotkey: 'Mod+Shift+Z',
      callback: () => redo(),
      options: { ignoreInputs: true, preventDefault: true },
    },
    // ── Snap toggle ──────────────────────────────────────────────────────────
    // 'S' is free — not used by any existing viewer binding. Uppercase is the
    // library's canonical letter form (matching is case-insensitive, so this
    // fires on a plain 's' press with no Shift).
    {
      hotkey: 'S',
      callback: () => setSnapSettings((prev) => ({ ...prev, enabled: !prev.enabled })),
      options: { ignoreInputs: true },
    },
  ]);
};
